import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Phone, ArrowRight, Loader2, Camera, Upload, Search, Filter,
  ShoppingBag, Heart, X, RefreshCw, AlertCircle, CheckCircle,
  ChevronLeft, ChevronRight, Image as ImageIcon, Sparkles,
  Package, Tag, DollarSign, Clock, Shield, Store
} from 'lucide-react';
import { publicStudioAPI } from '../../api';
import toast from 'react-hot-toast';
import CameraCapture from '../../components/CameraCapture';
import SizeRecommendPanel from '../../components/SizeRecommendPanel';
import { compressImageForUpload } from '../../utils/imageUtils';
import { MAX_UPLOAD_MB, MAX_UPLOAD_BYTES } from '../../config/uploadLimits';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ==================== Auth Screen ====================
const AuthScreen = ({ partnerInfo, onAuthenticated }) => {
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('phone'); // 'phone' | 'otp'
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const handleSendOtp = async () => {
    if (phone.length !== 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    setLoading(true);
    try {
      await publicStudioAPI.sendOtp({
        phone,
        studio_token: partnerInfo.token
      });
      toast.success('OTP sent to your phone');
      setStep('otp');
      setResendTimer(30);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== 4) {
      toast.error('Please enter the 4-digit OTP');
      return;
    }

    setLoading(true);
    try {
      const response = await publicStudioAPI.verifyOtp({
        phone,
        otp,
        studio_token: partnerInfo.token
      });
      toast.success('Welcome! Start your virtual try-on');
      onAuthenticated(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setLoading(true);
    try {
      await publicStudioAPI.resendOtp({ phone, studio_token: partnerInfo.token });
      toast.success('OTP resent');
      setResendTimer(30);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Partner Branding */}
        <div className="text-center mb-8">
          {partnerInfo.partner_logo_url ? (
            <img 
              src={partnerInfo.partner_logo_url} 
              alt={partnerInfo.partner_name}
              className="h-16 mx-auto mb-4 object-contain"
            />
          ) : (
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl mx-auto mb-4 flex items-center justify-center">
              <Store className="w-8 h-8 text-white" />
            </div>
          )}
          <h1 className="text-2xl font-bold text-white">{partnerInfo.partner_name}</h1>
          <p className="text-slate-400 mt-2">Virtual Try-On Experience</p>
        </div>

        {/* Auth Card */}
        <div className="bg-white rounded-2xl p-6 shadow-xl">
          {step === 'phone' ? (
            <>
              <h2 className="text-xl font-semibold text-slate-900 mb-2">Welcome!</h2>
              <p className="text-slate-600 text-sm mb-6">
                Enter your phone number to start trying on outfits virtually
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Phone Number
                  </label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 rounded-l-lg border border-r-0 border-slate-300 bg-slate-50 text-slate-500 text-sm">
                      +91
                    </span>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      placeholder="Enter 10-digit number"
                      className="flex-1 px-4 py-3 border border-slate-300 rounded-r-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      disabled={loading}
                    />
                  </div>
                </div>

                <button
                  onClick={handleSendOtp}
                  disabled={loading || phone.length !== 10}
                  className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-medium hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Get OTP <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </div>

              <p className="text-xs text-slate-500 mt-4 text-center">
                By continuing, you agree to our terms of service
              </p>
            </>
          ) : (
            <>
              <button
                onClick={() => setStep('phone')}
                className="text-slate-600 hover:text-slate-900 mb-4 flex items-center gap-1 text-sm"
              >
                <ChevronLeft className="w-4 h-4" /> Change number
              </button>

              <h2 className="text-xl font-semibold text-slate-900 mb-2">Enter OTP</h2>
              <p className="text-slate-600 text-sm mb-6">
                We've sent a 4-digit code to +91 {phone}
              </p>

              <div className="space-y-4">
                <div>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    placeholder="Enter 4-digit OTP"
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg text-center text-2xl tracking-widest focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={loading}
                    maxLength={4}
                  />
                </div>

                <button
                  onClick={handleVerifyOtp}
                  disabled={loading || otp.length !== 4}
                  className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-medium hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Verify & Continue <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>

                <button
                  onClick={handleResendOtp}
                  disabled={loading || resendTimer > 0}
                  className="w-full py-2 text-blue-600 text-sm disabled:text-slate-400"
                >
                  {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : 'Resend OTP'}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Trust Badges */}
        <div className="flex items-center justify-center gap-4 mt-6 text-slate-400 text-xs">
          <span className="flex items-center gap-1">
            <Shield className="w-4 h-4" /> Secure
          </span>
          <span className="flex items-center gap-1">
            <Clock className="w-4 h-4" /> {partnerInfo.session_duration_hours}h session
          </span>
        </div>
      </motion.div>
    </div>
  );
};

// ==================== Product Card ====================
const ProductCard = ({ product, onSelect, isSelected }) => {
  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => onSelect(product)}
      className={`bg-white rounded-xl overflow-hidden border-2 cursor-pointer transition-colors ${
        isSelected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-slate-200 hover:border-slate-300'
      }`}
    >
      <div className="aspect-[3/4] relative bg-slate-100">
        {product.tryon_input_image_url ? (
          <img
            src={product.tryon_input_image_url}
            alt={product.product_name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package className="w-12 h-12 text-slate-300" />
          </div>
        )}
        {isSelected && (
          <div className="absolute top-2 right-2 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
            <CheckCircle className="w-4 h-4 text-white" />
          </div>
        )}
      </div>
      <div className="p-3">
        <h3 className="font-medium text-slate-900 text-sm truncate">{product.product_name}</h3>
        <p className="text-xs text-slate-500 mt-0.5">SKU: {product.sku_id}</p>
        {product.price > 0 && (
          <p className="text-sm font-semibold text-slate-900 mt-1">₹{product.price?.toLocaleString()}</p>
        )}
      </div>
    </motion.div>
  );
};

// ==================== Main Studio Component ====================
const TryOnStudioMain = ({ session, partnerInfo }) => {
  const [inventory, setInventory] = useState([]);
  const [loadingInventory, setLoadingInventory] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [customerPhoto, setCustomerPhoto] = useState(null);
  const [customerPhotoUrl, setCustomerPhotoUrl] = useState(null);
  const [customerPhotoS3Key, setCustomerPhotoS3Key] = useState(null);
  const [tryonResult, setTryonResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [hardCart, setHardCart] = useState([]);
  
  // Filters
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Session status
  const [sessionStatus, setSessionStatus] = useState(null);

  // Add to hard cart — syncs with backend (which also records purchase intent)
  const handleAddToCart = async () => {
    if (!selectedProduct) return;
    const productId = selectedProduct.product_id || selectedProduct.id;
    if (hardCart.find(p => (p.product_id || p.id) === productId)) {
      toast('Already in your cart', { icon: '🛒' });
      return;
    }
    try {
      const response = await fetch(
        `${API_URL}/api/public-studio/session/hard-cart/add?session_token=${session.session_token}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ product_id: productId, quantity: 1 })
        }
      );
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to add to cart');
      }
      setHardCart(prev => [...prev, selectedProduct]);
      toast.success('Added to cart!');
    } catch (err) {
      toast.error(err.message || 'Failed to add to cart');
    }
  };

  // Fetch inventory
  const fetchInventory = useCallback(async () => {
    setLoadingInventory(true);
    try {
      const response = await publicStudioAPI.getInventory(session.session_token, {
        search: search || undefined,
        category: category || undefined,
        page,
        limit: 12,
        in_stock_only: true
      });
      setInventory(response.data.products || []);
      setCategories(response.data.categories || []);
      setTotalPages(response.data.total_pages || 1);
    } catch (error) {
      console.error('Failed to load inventory:', error);
      toast.error('Failed to load products');
    } finally {
      setLoadingInventory(false);
    }
  }, [session.session_token, search, category, page]);

  // Fetch session status
  const fetchSessionStatus = useCallback(async () => {
    try {
      const response = await publicStudioAPI.getSessionStatus(session.session_token);
      setSessionStatus(response.data);
    } catch (error) {
      console.error('Failed to get session status:', error);
    }
  }, [session.session_token]);

  useEffect(() => {
    fetchInventory();
    fetchSessionStatus();
  }, [fetchInventory, fetchSessionStatus]);

  // Handle photo upload (compresses image before sending to stay within Lambda limits)
  const handlePhotoUpload = async (file) => {
    if (!file) return;
    
    if (file.size > MAX_UPLOAD_BYTES) {
      toast.error(`Image must be less than ${MAX_UPLOAD_MB}MB`);
      return;
    }

    setLoading(true);
    // Reset tryon when a new photo is uploaded
    setTryonResult(null);
    setCustomerPhotoS3Key(null);
    try {
      // Compress to ≤ 1 MB before upload (WhatsApp-style)
      const compressed = await compressImageForUpload(file);

      // Show compressed preview immediately
      const compressedPreview = URL.createObjectURL(compressed);
      setCustomerPhotoUrl(compressedPreview);
      setCustomerPhoto(compressed);

      const formData = new FormData();
      formData.append('file', compressed);
      
      const response = await publicStudioAPI.uploadPhoto(session.session_token, formData);
      // Use backend's signed URL as the canonical preview
      setCustomerPhotoUrl(response.data.image_url || compressedPreview);
      setCustomerPhotoS3Key(response.data.s3_key);
      toast.success('Photo uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setLoading(false);
    }
  };

  // Handle camera capture
  const handleCameraCapture = async (file, previewUrl) => {
    setCameraOpen(false);
    // Show camera preview immediately for responsiveness; handlePhotoUpload will
    // replace it with the compressed version + backend signed URL
    setCustomerPhotoUrl(previewUrl);
    await handlePhotoUpload(file);
  };

  // Handle file input
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Show preview immediately before compression for responsiveness
    setCustomerPhotoUrl(URL.createObjectURL(file));
    await handlePhotoUpload(file);
  };

  // Handle try-on generation — calls real tryon-v2 API
  const handleGenerateTryon = async () => {
    if (!customerPhotoUrl || !selectedProduct) {
      toast.error('Please upload your photo and select a product');
      return;
    }
    if (!customerPhotoS3Key) {
      toast.error('Photo is still uploading, please wait');
      return;
    }
    if (sessionStatus && sessionStatus.tryons_used >= sessionStatus.max_tryons) {
      toast.error('You have reached the maximum try-ons for this session');
      return;
    }

    setLoading(true);
    try {
      const productId = selectedProduct.product_id;
      const response = await fetch(
        `${API_URL}/api/public-studio/session/tryon-v2?s3_key=${encodeURIComponent(customerPhotoS3Key)}&product_id=${encodeURIComponent(productId)}`,
        {
          method: 'POST',
          headers: { 'X-Session-Token': session.session_token }
        }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(
          typeof data.detail === 'object' ? (data.detail.message || 'Try-on failed') : (data.detail || 'Try-on failed')
        );
      }
      setTryonResult({
        url: data.tryon_url,
        s3_key: data.tryon_key,
        product: selectedProduct,
        cached: data.is_cache_hit
      });
      toast.success(data.is_cache_hit ? 'Loaded from cache!' : 'Try-on generated!');
      fetchSessionStatus();
    } catch (error) {
      toast.error(error.message || 'Failed to generate try-on');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {partnerInfo.partner_logo_url ? (
              <img 
                src={partnerInfo.partner_logo_url} 
                alt={partnerInfo.partner_name}
                className="h-8 object-contain"
              />
            ) : (
              <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                <Store className="w-5 h-5 text-white" />
              </div>
            )}
            <span className="font-semibold text-slate-900 hidden sm:block">{partnerInfo.partner_name}</span>
          </div>
          
          {sessionStatus && (
            <div className="flex items-center gap-3 text-sm">
              <span className="text-slate-500">
                {sessionStatus.tryons_used}/{sessionStatus.max_tryons} try-ons
              </span>
              <div className="w-20 h-2 bg-slate-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-blue-500 to-purple-600 transition-all"
                  style={{ width: `${(sessionStatus.tryons_used / sessionStatus.max_tryons) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Panel - Photo Upload & Try-On */}
          <div className="lg:col-span-1 space-y-4">
            {/* Your Photo */}
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <Camera className="w-5 h-5 text-blue-600" />
                Your Photo
              </h3>
              
              <div className="aspect-[3/4] rounded-lg border-2 border-dashed border-slate-300 overflow-hidden bg-slate-50">
                {customerPhotoUrl ? (
                  <div className="relative w-full h-full">
                    <img 
                      src={customerPhotoUrl} 
                      alt="Your photo"
                      className="w-full h-full object-cover"
                    />
                    <button
                      onClick={() => {
                        setCustomerPhoto(null);
                        setCustomerPhotoUrl(null);
                        setCustomerPhotoS3Key(null);
                        setTryonResult(null);
                      }}
                      className="absolute top-2 right-2 p-1.5 bg-white rounded-full shadow-md"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-4 p-4">
                    <div className="flex gap-2">
                      <button
                        onClick={() => setCameraOpen(true)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center gap-2 text-sm"
                      >
                        <Camera className="w-4 h-4" />
                        Take Photo
                      </button>
                      <label className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg flex items-center gap-2 cursor-pointer text-sm hover:bg-slate-200">
                        <Upload className="w-4 h-4" />
                        Upload
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleFileSelect}
                          className="hidden"
                        />
                      </label>
                    </div>
                    <p className="text-xs text-slate-500 text-center">
                      For best results, use a full-body photo with good lighting
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Selected Product */}
            {selectedProduct && (
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Package className="w-5 h-5 text-purple-600" />
                  Selected Product
                </h3>
                <div className="flex gap-3">
                  <img 
                    src={selectedProduct.tryon_input_image_url}
                    alt={selectedProduct.product_name}
                    className="w-20 h-24 object-cover rounded-lg"
                  />
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-slate-900 text-sm">{selectedProduct.product_name}</h4>
                    <p className="text-xs text-slate-500">SKU: {selectedProduct.sku_id}</p>
                    {selectedProduct.price > 0 && (
                      <p className="text-sm font-semibold text-slate-900 mt-1">
                        ₹{selectedProduct.price?.toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Generate Button */}
            <button
              onClick={handleGenerateTryon}
              disabled={!customerPhotoUrl || !selectedProduct || loading}
              className="w-full py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 hover:from-blue-700 hover:to-purple-700 transition-all"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  Generate Try-On
                </>
              )}
            </button>

            {/* Try-On Result */}
            {tryonResult && (
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <h3 className="font-semibold text-slate-900 mb-3">Your Virtual Try-On</h3>
                <div className="aspect-[3/4] rounded-lg overflow-hidden bg-slate-100">
                  <img 
                    src={tryonResult.url}
                    alt="Try-on result"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={handleAddToCart}
                    disabled={!selectedProduct}
                    data-testid="public-tryon-add-to-cart"
                    className="flex-1 py-2 bg-emerald-600 text-white rounded-lg text-sm flex items-center justify-center gap-2 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ShoppingBag className="w-4 h-4" />
                    {hardCart.find(p => (p.product_id || p.id) === (selectedProduct?.product_id || selectedProduct?.id)) ? 'In Cart' : 'Add to Cart'}
                  </button>
                  <button className="py-2 px-4 border border-slate-200 rounded-lg text-sm">
                    <Heart className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Size Recommendation Panel - shows after photo upload, no TryOn required */}
            {customerPhotoS3Key && selectedProduct?.size_chart && Object.keys(selectedProduct.size_chart || {}).length > 0 && (
              <SizeRecommendPanel
                key={selectedProduct.product_id}
                apiFetcher={async (heightCm) => {
                  const resp = await fetch(`${API_URL}/api/public-studio/session/size-recommend`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'X-Session-Token': session.session_token },
                    body: JSON.stringify({
                      customer_s3_key: customerPhotoS3Key,
                      height_cm: heightCm,
                      product_id: selectedProduct.product_id
                    })
                  });
                  const data = await resp.json();
                  if (!resp.ok) throw new Error(
                    typeof data.detail === 'object' ? (data.detail.message || 'Failed') : (data.detail || 'Failed')
                  );
                  return data;
                }}
                productId={selectedProduct.product_id}
                garment={selectedProduct}
                onResult={() => {}}
              />
            )}
          </div>

          {/* Right Panel - Inventory */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                {/* Search */}
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPage(1);
                    }}
                    placeholder="Search by name or SKU..."
                    className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm"
                  />
                </div>
                
                {/* Category Filter */}
                {categories.length > 0 && (
                  <select
                    value={category}
                    onChange={(e) => {
                      setCategory(e.target.value);
                      setPage(1);
                    }}
                    className="px-4 py-2 border border-slate-200 rounded-lg text-sm"
                  >
                    <option value="">All Categories</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                )}
              </div>

              {/* Product Grid */}
              {loadingInventory ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div>
              ) : inventory.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No products found</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {inventory.map(product => (
                      <ProductCard
                        key={product.product_id}
                        product={product}
                        onSelect={setSelectedProduct}
                        isSelected={selectedProduct?.product_id === product.product_id}
                      />
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-2 mt-6">
                      <button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="p-2 border border-slate-200 rounded-lg disabled:opacity-50"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <span className="text-sm text-slate-600">
                        Page {page} of {totalPages}
                      </span>
                      <button
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        className="p-2 border border-slate-200 rounded-lg disabled:opacity-50"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Camera Capture Modal */}
      <CameraCapture
        isOpen={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onCapture={handleCameraCapture}
        aspectRatio="3/4"
      />
    </div>
  );
};

// ==================== Main Component ====================
const PublicTryOnStudio = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [partnerInfo, setPartnerInfo] = useState(null);
  const [session, setSession] = useState(null);

  useEffect(() => {
    const validateLink = async () => {
      try {
        const response = await publicStudioAPI.validateLink(token);
        setPartnerInfo({ ...response.data, token });
      } catch (err) {
        setError(err.response?.data?.detail || 'Invalid or expired link');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      validateLink();
    }
  }, [token]);

  const handleAuthenticated = (sessionData) => {
    setSession(sessionData);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-slate-900 mb-2">Link Unavailable</h1>
          <p className="text-slate-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-slate-900 text-white rounded-lg"
          >
            Go to Homepage
          </button>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <AuthScreen 
        partnerInfo={partnerInfo} 
        onAuthenticated={handleAuthenticated} 
      />
    );
  }

  return (
    <TryOnStudioMain 
      session={session} 
      partnerInfo={partnerInfo}
    />
  );
};

export default PublicTryOnStudio;
